import {FC} from "react";
import {useSelector} from "react-redux";
import {getCurrentSchedules} from "../../../../../../../redux/selectors/patientsSelector";
import {IMedication} from "../../../../../../../utils/interfaces";
import {Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {Add} from "../../../../../../../assets/icons/Add";
import {Edit} from "../../../../../../../assets/icons/Edit";
import {Delete} from "../../../../../../../assets/icons/Delete";
import classes from './MedicationsTable.module.scss';
import {differenceInCalendarDays, format, parse} from "date-fns";

type MedicationsTableProps = {
  onEditMedication: (medication: IMedication) => void;
  onRemoveMedication: (medication: IMedication) => void;
  onAddMedication: () => void;
}

export const MedicationsTable: FC<MedicationsTableProps> = ({onAddMedication, onEditMedication, onRemoveMedication}) => {
  const patientMedications: IMedication[] = useSelector(getCurrentSchedules)

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
      <Button
        onClick={onAddMedication}
        startIcon={<Add />}
        variant="contained"
        sx={{alignSelf: 'flex-start'}}
      >
        Add medication
      </Button>
      <TableContainer sx={{height: 430}} component={Paper}>
        <Table stickyHeader sx={{minWidth: 650}} aria-label="simple table">
          <TableHead>
            <TableRow classes={{
              root: classes.tableHeader
            }}>
              <TableCell>Medication Name</TableCell>
              <TableCell align="left">Dosage</TableCell>
              <TableCell align="center">Times</TableCell>
              <TableCell align="right">Start Date</TableCell>
              <TableCell align="right">End Date</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patientMedications.map((medication) => {
              const startDate = parse(medication.startDate, 'yyyy-MM-dd', new Date());
              const endDate = parse(medication.endDate, 'yyyy-MM-dd', new Date());
              const daysDifference = differenceInCalendarDays(endDate, startDate) + 1;

              return (
                <TableRow
                  key={medication.id}
                  sx={{'&:last-child td, &:last-child th': {border: 0}}}
                >
                  <TableCell component="th" scope="row">
                    {medication.name}
                  </TableCell>
                  <TableCell align="left">{medication.dosageValue} {medication.dosageUnit}</TableCell>
                  <TableCell align="left">
                    <div style={{display: 'flex', flexDirection: 'row', gap: 8}}>
                      {medication.times.map((time, index) => {
                        const splittedTime = time.split(':');
                        const formattedTime = `${splittedTime[0]}:${splittedTime[1]}`;

                        return (
                          <Chip size="small" key={index} label={formattedTime} variant="outlined" />
                        )
                      })}
                    </div>
                  </TableCell>
                  <TableCell align="right">{format(startDate, 'dd/MM/yyyy')}</TableCell>
                  <TableCell align="right">{format(endDate, 'dd/MM/yyyy')}</TableCell>
                  <TableCell align="right">{daysDifference} day{daysDifference > 1 ? 's' : ''}</TableCell>
                  <TableCell align="right">
                    <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
                      <Button sx={{marginRight: 2}} variant="contained" onClick={() => onEditMedication(medication)}>
                        <Edit className={classes.editIcon} />
                      </Button>
                      <Button variant="outlined" onClick={() => onRemoveMedication(medication)}>
                        <Delete />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}